import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import isArray from 'lodash/isArray';

import { makeStyles, useTheme } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import { useMediaQuery, Grid } from '@material-ui/core';
import { useTranslation } from '../../src/i18n';
import Button from '../Button';
import { fontSize, colors } from '../../constants/styleConstants';
import FbPagination from '../FbPagination';

export const useFBTableStyles = makeStyles(theme => ({
  table: {
    width: '100%',
    overflow: 'auto',
  },
  cell: {
    verticalAlign: 'middle',
    padding: 10,
    fontSize: fontSize.table,
    borderBottom: `1px solid ${colors.linesGray}`,
    position: 'relative',
  },
  flexCell: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  flexInlineCell: {
    display: 'flex',
    alignItems: 'center',
  },
  head: {
    height: '2em',
    fontSize: fontSize.table,
    padding: 8,
    lineHeight: '1em',
    borderTop: `1px solid ${colors.linesGray}`,
    borderBottom: `1px solid ${colors.linesGray}`,
    color: colors.secondaryTitleColor,
  },
  link: {
    color: colors.blue,
    textDecoration: 'underline',
  },
  seeMoreButton: {
    width: '100%',
    backgroundColor: colors.background.gray,
    color: colors.blue,
    [theme.breakpoints.down('sm')]: {
      backgroundColor: 'transparent',
    },
  },
  bold: {
    fontWeight: 'bold',
  },
  avatar: {
    width: 45,
    height: 45,
    borderRadius: '50%',
    marginRight: 20,
  },
  flex: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  artistName: {
    width: '25%',
    maxWidth: 220,
  },
}));

const FbTable = ({ headers, data, cells, customElements, fetchFunction, withPagination, noButton }) => {
  const classes = useFBTableStyles();
  const { t } = useTranslation();
  const theme = useTheme();
  const mobileView = useMediaQuery(theme.breakpoints.down('sm'));

  const [tableData, setData] = React.useState(get(data, 'data', []));
  const [isViewAllClicked, setViewAll] = React.useState(false);
  const [isCollapsed, setCollapsed] = React.useState(true);

  // for first fetch
  useEffect(() => {
    const entryData = get(data, 'data', []);
    if (withPagination || noButton) {
      setData(entryData);
    } else {
      setData(isCollapsed ? entryData.slice(0, 4) : entryData);
    }
  }, [data, isCollapsed, withPagination, noButton]);

  const getValueFromArray = (item, array) => {
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < array.length; i++) {
      const value = get(item, array[i]);
      if (value) return value;
    }
    return null;
  };

  // eslint-disable-next-line no-nested-ternary
  const customRow = mobileView
    ? customElements
      ? customElements.card
      : null
    : customElements
      ? customElements.row
      : null;
  const { limit, total, page } = data;

  if (tableData.length === 0) {
    return t('operabase.general.NO_RESULTS');
  }

  const showMore = () => {
    // fetch additional data for the first click
    if (isViewAllClicked) {
      setData(isCollapsed ? get(data, 'data', []) : tableData.slice(0, 4));
    } else {
      fetchFunction({ limit: get(data, 'total') });
      setViewAll(true);
    }
    setCollapsed(!isCollapsed);
  };

  return (
    <>
      {mobileView ? (
        <Grid container spacing={2}>
          {tableData &&
            tableData.length > 0 &&
            tableData.map(card => (
              <Grid key={get(data, 'id')} item sm={12} xs={12}>
                {React.cloneElement(customRow, {
                  data: card,
                })}
              </Grid>
            ))}
        </Grid>
      ) : (
        <div className={classes.table}>
          <Table>
            <TableHead>
              <TableRow>
                {headers &&
                  headers.length > 0 &&
                  headers.map(item => (
                    <TableCell classes={{ head: classes.head }} key={get(item, 'props.id') || item}>
                      {item}
                    </TableCell>
                  ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {tableData &&
                tableData.length > 0 &&
                tableData.map((row, index) =>
                  customRow ? (
                    React.cloneElement(customRow, {
                      data: row,
                      cellClass: classes.cell,
                      key: get(row, 'id') || `FbTable-${index}`
                    })
                  ) : (
                    <TableRow key={get(row, 'id') || index}>
                      {cells.map(item => (
                        <TableCell className={classes.cell} key={get(row, item) || get(row, `[0].${item}`)}>
                          {isArray(item) ? getValueFromArray(row, item) : get(row, item, '')}
                        </TableCell>
                      ))}
                    </TableRow>
                  ),
                )}
            </TableBody>
          </Table>
        </div>
      )}
      {withPagination && <FbPagination filters={{ limit, total, page }} fetchFunction={fetchFunction} />}
      {!withPagination && !noButton && (
        <Button onClick={showMore} className={classes.seeMoreButton}>
          {t(isCollapsed ? 'operabase.general.m_ARTICLES_SEE_ALL' : 'operabase.general.FN_SEE_LESS_TITLE')}
        </Button>
      )}
    </>
  );
};

FbTable.propTypes = {
  data: PropTypes.object,
  headers: PropTypes.array,
  cells: PropTypes.array,
  customElements: PropTypes.shape({
    card: PropTypes.element,
    row: PropTypes.element,
  }),
  fetchFunction: PropTypes.func,
  withPagination: PropTypes.bool,
  noButton: PropTypes.bool,
};

FbTable.defaultProps = {
  data: {
    total: 0,
    limit: 100,
    page: 1,
    data: [],
  },
  headers: [],
  withPagination: true,
  fetchFunction: () => {},
  noButton: false,
};

export default FbTable;
